import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Reusable Badge component for tags, status indicators, and labels
 * Supports multiple color variants matching portfolio theme
 */
const Badge = forwardRef(({
  children,
  variant = 'default',
  size = 'md',
  className = '',
  icon: Icon,
  removable = false,
  onRemove,
  ...props
}, ref) => {

  // Base styles
  const baseStyles = 'inline-flex items-center gap-1 font-medium rounded-full transition-all duration-200';

  // Variant styles using portfolio colors
  const variants = {
    default: 'bg-white/10 text-white border border-white/20',
    purple: 'bg-primary-500/20 text-primary-300 border border-primary-500/30',
    blue: 'bg-secondary-500/20 text-secondary-300 border border-secondary-500/30',
    green: 'bg-portfolio-green/20 text-success-300 border border-success-500/30',
    orange: 'bg-portfolio-orange/20 text-orange-300 border border-orange-500/30',
    red: 'bg-error-500/20 text-error-300 border border-error-500/30',
    yellow: 'bg-warning-500/20 text-warning-300 border border-warning-500/30',
    pink: 'bg-accent-500/20 text-accent-300 border border-accent-500/30',
    teal: 'bg-teal-500/20 text-teal-300 border border-teal-500/30',
    // Solid variants
    'purple-solid': 'bg-primary-600 text-white',
    'blue-solid': 'bg-secondary-600 text-white',
    'green-solid': 'bg-portfolio-green text-white',
    'orange-solid': 'bg-portfolio-orange text-white',
    'red-solid': 'bg-error-600 text-white',
    // Outline variants
    'purple-outline': 'bg-transparent text-primary-300 border border-primary-500',
    'blue-outline': 'bg-transparent text-secondary-300 border border-secondary-500',
    'green-outline': 'bg-transparent text-success-300 border border-success-500',
  };

  // Size styles
  const sizes = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2 py-1',
    lg: 'text-sm px-3 py-1.5',
  };

  // Icon sizes
  const iconSizes = {
    sm: 12,
    md: 14,
    lg: 16,
  };

  // Combine all styles
  const badgeClasses = `${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`;

  // Handle remove click
  const handleRemove = (e) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove();
    }
  };

  return (
    <span
      ref={ref}
      className={badgeClasses}
      {...props}
    >
      {Icon && (
        <Icon size={iconSizes[size]} aria-hidden="true" />
      )}

      {children}

      {removable && (
        <button
          type="button"
          onClick={handleRemove}
          className="ml-0.5 hover:bg-white/20 rounded-full p-0.5 transition-colors focus:outline-none focus:ring-1 focus:ring-white/50"
          aria-label="Remove"
        >
          <svg
            width={iconSizes[size]}
            height={iconSizes[size]}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </span>
  );
});

Badge.displayName = 'Badge';

Badge.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf([
    'default',
    'purple', 'blue', 'green', 'orange', 'red', 'yellow', 'pink', 'teal',
    'purple-solid', 'blue-solid', 'green-solid', 'orange-solid', 'red-solid',
    'purple-outline', 'blue-outline', 'green-outline',
  ]),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  icon: PropTypes.elementType,
  removable: PropTypes.bool,
  onRemove: PropTypes.func,
};

/**
 * BadgeGroup component for displaying multiple badges together
 */
export const BadgeGroup = ({ children, className = '', spacing = 'normal' }) => {
  const spacingStyles = {
    tight: 'gap-1',
    normal: 'gap-2',
    loose: 'gap-3',
  };

  return (
    <div className={`flex flex-wrap items-center ${spacingStyles[spacing]} ${className}`}>
      {children}
    </div>
  );
};

BadgeGroup.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  spacing: PropTypes.oneOf(['tight', 'normal', 'loose']),
};

export default Badge;
